import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Download, Share2, Loader2, Heart, Flag, X, UserX } from 'lucide-react';
import lightGallery from 'lightgallery';
import lgThumbnail from 'lightgallery/plugins/thumbnail';
import type { LightGallery } from 'lightgallery/lightgallery';
import type { GalleryItem } from 'lightgallery/lg-utils';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-thumbnail.css';
import { AlbumOverlayConfig, Photo } from '../../types';
import { compositePhotoToBlob, compositePhotoToObjectUrl } from '../../lib/clientCompositor';
import styles from './AlbumPhotoViewer.module.css';

interface AlbumPhotoViewerProps {
  photos: Photo[];
  initialIndex: number;
  onClose: () => void;
  /** Fallback for non-native-share environments (desktop). Opens custom ShareSheet. */
  onShare?: (photoId: string) => void;
  onReport?: (photoId: string) => void;
  onToggleLike?: (photoId: string) => void;
  /**
   * "Not me" — My Photos tab only. Tells the parent this face match is wrong;
   * the parent drops it from My Photos and the algorithm learns to stop showing
   * it. The viewer closes after firing (the photo is gone from the grid behind).
   */
  onNotMe?: (photoId: string) => void;
  photoMeta?: Record<string, { likeCount?: number; isLikedByMe?: boolean }>;
  albumName?: string;
  eventName?: string;
  /**
   * When true, composite overlays onto the original client-side and show that
   * instead of `processedUrl`. Used as a fallback when the server-side
   * compositor failed (Album.processingStatus === 'failed').
   */
  useClientCompositor?: boolean;
  /** Overlay configs from the album — required when useClientCompositor is on. */
  overlayConfigs?: AlbumOverlayConfig[];
  /** Actual total photo count (may exceed photos.length when paginating). */
  totalPhotos?: number;
}

// S3 presigned URLs cannot be fetched cross-origin from pixelmachha.com.
// CloudFront has proper CORS headers.
function toCdnUrl(url: string): string {
  try {
    const parsed = new URL(url);
    if (parsed.hostname.includes('pixel-machha-photos.s3')) {
      return `https://d3ic3ju44zc444.cloudfront.net${parsed.pathname}`;
    }
  } catch {
    // not a valid URL
  }
  return url;
}

// Thumbnail strip height (matches thumbHeight option below)
const THUMB_HEIGHT = 72;
// Gap between action capsule and thumbnail strip
const THUMB_GAP = 28;

export function AlbumPhotoViewer({
  photos,
  initialIndex,
  onClose,
  onShare,
  onReport,
  onToggleLike,
  onNotMe,
  photoMeta,
  albumName,
  eventName,
  useClientCompositor = false,
  overlayConfigs,
  totalPhotos,
}: AlbumPhotoViewerProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const lgRef = useRef<LightGallery | null>(null);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const currentIndexRef = useRef(initialIndex);
  const [lgIsOpen, setLgIsOpen] = useState(false);
  const [isSharing, setIsSharing] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [compositing, setCompositing] = useState(false);

  // Stable ref for onClose so the init effect never needs to re-run
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;


  // Per-photo composited object URLs — generated lazily on navigation.
  const compositedUrlsRef = useRef<Map<string, string>>(new Map());

  const hasOverlays = !!overlayConfigs && overlayConfigs.length > 0;

  const overlaySafeUrl = useCallback(
    (p: Photo): string => {
      if (p.processedUrl) return p.processedUrl;
      if (hasOverlays) return p.processedThumbnailUrl || '';
      return p.originalUrl || p.thumbnailUrl || '';
    },
    [hasOverlays],
  );

  // Build the slide list, incorporating any already-composited URLs.
  const buildSlides = useCallback((): GalleryItem[] => {
    return photos.map((p) => {
      let src = overlaySafeUrl(p);
      if (useClientCompositor && hasOverlays && !p.processedUrl && !p.processedThumbnailUrl) {
        const composited = compositedUrlsRef.current.get(p._id);
        if (composited) src = composited;
      }
      return {
        src: src || '',
        thumb: p.processedThumbnailUrl || p.thumbnailUrl || src || '',
      };
    });
  }, [photos, overlaySafeUrl, useClientCompositor, hasOverlays]);

  // Initialise lightgallery once on mount and clean up on unmount.
  useEffect(() => {
    if (!containerRef.current) return;
    const container = containerRef.current;

    const handleAfterSlide = (e: Event) => {
      const idx = (e as CustomEvent).detail.index as number;
      setCurrentIndex(idx);
      currentIndexRef.current = idx;
    };
    const handleAfterOpen = () => {
      document.body.style.overflow = 'hidden';
      setLgIsOpen(true);
    };
    const handleBeforeClose = () => {
      setLgIsOpen(false);
    };
    const handleAfterClose = () => {
      document.body.style.overflow = '';
      onCloseRef.current();
    };

    container.addEventListener('lgAfterSlide', handleAfterSlide);
    container.addEventListener('lgAfterOpen', handleAfterOpen);
    container.addEventListener('lgBeforeClose', handleBeforeClose);
    container.addEventListener('lgAfterClose', handleAfterClose);

    lgRef.current = lightGallery(container, {
      dynamic: true,
      dynamicEl: buildSlides(),
      plugins: [lgThumbnail],
      speed: 300,
      preload: 5,
      download: false,
      escKey: true,
      keyPress: true,
      closeOnTap: true,
      swipeToClose: true,
      licenseKey: '0000-0000-000-0000',
      // Thumbnail strip settings
      thumbnail: true,
      animateThumb: true,
      toggleThumb: false,
      enableThumbDrag: true,
      enableThumbSwipe: true,
      thumbWidth: 80,
      thumbHeight: `${THUMB_HEIGHT}px`,
    });

    // Defer openGallery by one task so the click event that triggered this
    // mount has fully propagated before lightgallery's closeOnTap listener
    // becomes active — otherwise the same click immediately closes the gallery.
    const openTimer = setTimeout(() => lgRef.current?.openGallery(initialIndex), 50);

    return () => {
      clearTimeout(openTimer);
      container.removeEventListener('lgAfterSlide', handleAfterSlide);
      container.removeEventListener('lgAfterOpen', handleAfterOpen);
      container.removeEventListener('lgBeforeClose', handleBeforeClose);
      container.removeEventListener('lgAfterClose', handleAfterClose);
      lgRef.current?.destroy();
      lgRef.current = null;
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Revoke composited object URLs on unmount.
  useEffect(() => {
    return () => {
      for (const url of compositedUrlsRef.current.values()) URL.revokeObjectURL(url);
      compositedUrlsRef.current.clear();
    };
  }, []);

  // Lazily composite the current photo when the client-side fallback is active.
  const photo = photos[currentIndex];
  const shouldComposite =
    useClientCompositor &&
    hasOverlays &&
    !photo?.processedUrl &&
    !photo?.processedThumbnailUrl &&
    !!photo?.originalUrl;

  useEffect(() => {
    if (!shouldComposite || !photo) return;
    if (compositedUrlsRef.current.has(photo._id)) return;

    let cancelled = false;
    setCompositing(true);
    compositePhotoToObjectUrl({
      baseUrl: photo.originalUrl!,
      overlayConfigs: overlayConfigs!,
    })
      .then((url) => {
        if (cancelled) { URL.revokeObjectURL(url); return; }
        compositedUrlsRef.current.set(photo._id, url);
        if (lgRef.current) {
          lgRef.current.updateSlides(buildSlides(), currentIndexRef.current);
        }
      })
      .catch((err) => console.error('[AlbumPhotoViewer] Client-side composite failed:', err))
      .finally(() => { if (!cancelled) setCompositing(false); });

    return () => { cancelled = true; };
  }, [shouldComposite, photo, overlayConfigs, buildSlides]);

  const handleDownload = async () => {
    if (isDownloading || !photo) return;
    setIsDownloading(true);
    try {
      if (shouldComposite && photo.originalUrl) {
        try {
          const blob = await compositePhotoToBlob({
            baseUrl: photo.originalUrl,
            overlayConfigs: overlayConfigs!,
          });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = 'pixel-machha-' + photo.originalFilename.replace(/\.[^.]+$/, '') + '.jpg';
          a.click();
          URL.revokeObjectURL(url);
          return;
        } catch (err) {
          console.error('[AlbumPhotoViewer] Client-side composite for download failed:', err);
        }
      }
      const downloadUrl = toCdnUrl(overlaySafeUrl(photo));
      if (!downloadUrl) return;
      const res = await fetch(downloadUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'pixel-machha-' + photo.originalFilename.replace(/\.[^.]+$/, '') + '.jpg';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleShare = async () => {
    if (isSharing || !photo) return;
    setIsSharing(true);
    try {
      if (navigator.share) {
        const shareUrl = toCdnUrl(overlaySafeUrl(photo));
        const filename = `${(albumName ?? 'photo').replace(/\s+/g, '-').toLowerCase()}-photo.jpg`;
        const text = eventName && albumName
          ? `Hey ! Check out this photo from album "${albumName}" from the event "${eventName}" from PixelMachha.com`
          : albumName
          ? `Hey ! Check out this photo from album "${albumName}" from PixelMachha.com`
          : `Hey ! Check out this photo from PixelMachha.com`;
        try {
          const controller = new AbortController();
          const fetchTimeout = setTimeout(() => controller.abort(), 8000);
          const res = await fetch(shareUrl, { signal: controller.signal });
          clearTimeout(fetchTimeout);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          const blob = await res.blob();
          const file = new File([blob], filename, { type: 'image/jpeg' });
          const shareData: ShareData = { text, files: [file] };
          if (navigator.canShare?.(shareData)) {
            await navigator.share(shareData);
          } else {
            await navigator.share({ text, url: shareUrl });
          }
          return;
        } catch (err) {
          if ((err as Error)?.name === 'AbortError') return;
        }
      }
      onShare?.(photo._id);
    } finally {
      // Defer reset so React renders the loading state before clearing it.
      // Without this, synchronous paths (no navigator.share) batch true→false
      // into one render and the spinner never shows.
      setTimeout(() => setIsSharing(false), 350);
    }
  };

  const currentMeta = photo ? photoMeta?.[photo._id] : undefined;
  const displayTotal = totalPhotos ?? photos.length;

  // Memoise action capsule — only re-renders when the display state changes
  const actionCapsule = useMemo(() => {
    if (!photo) return null;
    const bottomOffset = THUMB_HEIGHT + THUMB_GAP;
    return (
      <div className={styles['bottom-area']} style={{ bottom: `${bottomOffset}px` }}>
        {compositing && shouldComposite && (
          <div className={styles['compositing-label']}>
            <Loader2 size={14} className={styles.spinner} />
            Applying overlays…
          </div>
        )}

        <div className={styles['action-capsule']}>
          {onToggleLike && (
            <>
              <motion.button
                className={[
                  styles['capsule-btn'],
                  currentMeta?.isLikedByMe ? styles['capsule-btn-liked'] : '',
                ].filter(Boolean).join(' ')}
                onClick={() => onToggleLike(photo._id)}
                aria-label={currentMeta?.isLikedByMe ? 'Unlike photo' : 'Like photo'}
                whileTap={{ scale: 0.88 }}
                transition={{ type: 'spring', stiffness: 500, damping: 28 }}
              >
                <Heart size={20} strokeWidth={1.5} fill={currentMeta?.isLikedByMe ? 'currentColor' : 'none'} />
                {(currentMeta?.likeCount ?? 0) > 0 && (
                  <span className={styles['capsule-count']}>{currentMeta!.likeCount}</span>
                )}
              </motion.button>
              <div className={styles['capsule-divider']} />
            </>
          )}

          <motion.button
            className={styles['capsule-btn']}
            onClick={handleShare}
            aria-label="Share photo"
            whileTap={{ scale: 0.88 }}
            transition={{ type: 'spring', stiffness: 500, damping: 28 }}
          >
            {isSharing
              ? <Loader2 size={20} strokeWidth={1.5} className={styles.spinner} />
              : <Share2 size={20} strokeWidth={1.5} />
            }
          </motion.button>

          <motion.button
            className={styles['capsule-btn']}
            onClick={handleDownload}
            aria-label="Download photo"
            whileTap={{ scale: 0.88 }}
            transition={{ type: 'spring', stiffness: 500, damping: 28 }}
          >
            {isDownloading
              ? <Loader2 size={20} strokeWidth={1.5} className={styles.spinner} />
              : <Download size={20} strokeWidth={1.5} />
            }
          </motion.button>

          {onReport && (
            <>
              <div className={styles['capsule-divider']} />
              <motion.button
                className={[styles['capsule-btn'], styles['capsule-btn-report']].join(' ')}
                onClick={() => onReport(photo._id)}
                aria-label="Report photo"
                whileTap={{ scale: 0.88 }}
                transition={{ type: 'spring', stiffness: 500, damping: 28 }}
              >
                <Flag size={18} strokeWidth={1.5} />
              </motion.button>
            </>
          )}

          {onNotMe && (
            <>
              <div className={styles['capsule-divider']} />
              <motion.button
                className={[styles['capsule-btn'], styles['capsule-btn-notme']].join(' ')}
                onClick={() => { onNotMe(photo._id); lgRef.current?.closeGallery(); }}
                aria-label="Not me — remove this photo from my photos"
                whileTap={{ scale: 0.88 }}
                transition={{ type: 'spring', stiffness: 500, damping: 28 }}
              >
                <UserX size={18} strokeWidth={1.75} />
                <span className={styles['capsule-btn-label']}>Not me</span>
              </motion.button>
            </>
          )}
        </div>

        <div className={styles['counter']}>
          {currentIndex + 1} / {displayTotal}
        </div>
      </div>
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photo, currentIndex, displayTotal, currentMeta, isSharing, isDownloading, compositing, shouldComposite, onToggleLike, onReport, onNotMe]);

  return (
    <>
      {/* Hidden container — lightgallery attaches to this element */}
      <div ref={containerRef} style={{ display: 'none' }} />

      {/* Custom action overlay — fixed over lightgallery, above thumbnail strip */}
      {lgIsOpen && createPortal(
        <>
          {/* Close button — always visible top-right, above everything */}
          <button
            className={styles['close-btn']}
            onClick={() => lgRef.current?.closeGallery()}
            aria-label="Close viewer"
          >
            <X size={20} strokeWidth={2} />
          </button>
          {actionCapsule}
        </>,
        document.body,
      )}
    </>
  );
}
